// Rider session on the website: the token and user live in localStorage
// (userToken / userData, as the booking flow and dashboard expect).
// Components subscribe with useSession(); call notifySessionChange() after
// writing or clearing them so every subscriber updates at once.
import { useEffect, useState } from 'react';

const EVENT = 'rooldrive:session';
const SIGNED_OUT = { token: null, user: null };

// True while React hydrates a prerendered page (src/index.js). Prerendered HTML is always rendered
// signed out, so the first render must be signed out too; the real session is read right after.
export const hydration = { active: false };

export function readSession() {
  try {
    const token = localStorage.getItem('userToken');
    const user = JSON.parse(localStorage.getItem('userData') || 'null');
    return token && user && user.id ? { token, user } : SIGNED_OUT;
  } catch {
    return SIGNED_OUT;
  }
}

export function notifySessionChange() {
  window.dispatchEvent(new Event(EVENT));
}

export function saveSession(token, user) {
  localStorage.setItem('userToken', token);
  localStorage.setItem('userData', JSON.stringify(user));
  notifySessionChange();
}

export function signOut() {
  localStorage.removeItem('userToken');
  localStorage.removeItem('userData');
  notifySessionChange();
}

export function useSession() {
  const [session, setSession] = useState(() => (hydration.active ? SIGNED_OUT : readSession()));

  useEffect(() => {
    const update = () => setSession(readSession());
    if (session === SIGNED_OUT) update();
    const onStorage = (e) => {
      if (e.key === null || e.key === 'userToken' || e.key === 'userData') update();
    };
    window.addEventListener(EVENT, update);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, update);
      window.removeEventListener('storage', onStorage);
    };
    // Subscribe once; `session` is only read for the first check above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return session;
}
